import type { CartItem, ConfigurationFlowId, InputImageDTO, OrderAddonDTO, OrderProductDTO, ResolvedCatalogue, ResolvedOption, ResolvedVariant } from 'models/purchaseFlowRevamp'

import { buildConfiguration } from './serializeLineConfiguration'
import { compact } from './wire'

/**
 * The resolved variant a cart line names, across all shells. A shell without `variants` (a partial
 * fixture or a container node) is skipped rather than thrown on, because every step calls this
 * during render.
 */
export function findVariant(catalogue: ResolvedCatalogue | undefined, variantId: string): ResolvedVariant | undefined {
  if (!catalogue) return undefined
  for (const shell of catalogue.shellProducts) {
    const v = (shell.variants ?? []).find((variant) => variant.id === variantId)
    if (v) return v
  }
  return undefined
}

/**
 * One cart line's add-ons, resolved to `productCode`. Shared with the instruction-options and
 * payment-intent requests, so every request names the same add-ons the order bills.
 *
 *   - `optionValues` (the drawer's add-on toggles): BOOLEAN true → 1, NUMBER > 0 → its value; ENUM
 *     shapes the variant choice and is not an add-on product.
 *   - `extraServiceIds` (product extensions such as the 3D floor-plan upgrade): NUMBER extras carry
 *     the chosen count, BOOLEAN ones 1.
 */
export function buildAddons(item: CartItem, variant: ResolvedVariant | undefined, catalogue: ResolvedCatalogue | undefined): OrderAddonDTO[] {
  const addons: OrderAddonDTO[] = []

  const optionsById = new Map<string, ResolvedOption>((variant?.options ?? []).map((o) => [o.id, o]))
  for (const [optionId, value] of Object.entries(item.optionValues ?? {})) {
    const option = optionsById.get(optionId)
    if (!option) continue

    if (option.kind === 'BOOLEAN') {
      if (value === true) addons.push({ productCode: option.productCode, count: 1 })
    } else if (option.kind === 'NUMBER') {
      const count = typeof value === 'number' ? value : Number(value)
      if (Number.isFinite(count) && count > 0) addons.push({ productCode: option.productCode, count })
    }
  }

  const extraById = new Map((catalogue?.extras ?? []).map((e) => [e.id, e]))
  for (const extraId of item.extraServiceIds ?? []) {
    const extra = extraById.get(extraId)
    if (!extra) continue
    // A NUMBER extra is priced per unit and the drawer quoted the chosen count,
    // so sending 1 would charge less than the client was shown. A BOOLEAN one
    // is billed once however many times it was clicked — ordering more of it
    // delivers nothing extra.
    const chosen = item.extraQuantities?.[extraId]
    const count = extra.kind === 'NUMBER' ? Math.max(1, Math.floor(chosen ?? 1)) : 1
    addons.push({ productCode: extra.productCode, count })
  }

  return addons
}

/**
 * The client-uploaded images a line edits, as session-scoped filenames.
 *
 * The refs are the GCS filenames the upload returned (`CartAttachment.gcFilename`
 * = `PfV2StagingVisual.id`), which is what the BE keys the session's files by —
 * NOT the name the file had on the client's disk.
 *
 * The BE bounds the set per product (`inputImagePolicy`, and
 * `V2OrderValidator.validateConfigurations` enforces it), so this applies the
 * same rule rather than sending something the order will be rejected for:
 *   - `UNLIMITED`      — send them all (2D floor-plan editing, slideshow).
 *   - `EXACTLY_COUNT`  — one input per produced item, so send them only when the
 *                        count matches. The drawer keeps the line's quantity in
 *                        step with the uploads for exactly this reason.
 *   - `NONE` / unknown — send none.
 *
 * An empty list is always valid on the wire — that is the documented case for
 * staging bought as an add-on to a photo package, where the photos to stage do
 * not exist yet at order time.
 */
function buildInputImages(
  item: CartItem,
  variant: ResolvedVariant | undefined,
  attachments: Record<string, ReadonlyArray<{ gcFilename: string }>>
): InputImageDTO[] {
  const policy = variant?.inputImagePolicy
  if (!policy || policy === 'NONE') return []

  const refs = (attachments[item.id] ?? []).map((attachment) => attachment.gcFilename).filter(Boolean)
  if (refs.length === 0) return []

  // One input per produced item. A mismatch would make the BE reject the whole
  // order, and an empty list is always valid, so the safe answer is to send none
  // — this file stays pure (see the header), so it cannot report the drop. The
  // drawer is what keeps the two in step, and it is where a mismatch is prevented
  // rather than detected.
  if (policy === 'EXACTLY_COUNT' && refs.length !== item.quantity) return []

  return refs.map((ref) => ({ ref }))
}

/**
 * The configuration flows a line's product announces: the bought variant's own, else its shell's
 * union. Mirrors the configuration step without its feature-flagged FE fallback, which the v2
 * catalogue leaves inert.
 */
function lineConfigurationFlows(
  item: CartItem,
  variant: ResolvedVariant,
  catalogue: ResolvedCatalogue | undefined
): ConfigurationFlowId[] {
  if (variant.configurationFlows && variant.configurationFlows.length > 0) return variant.configurationFlows
  return catalogue?.shellProducts.find((shell) => shell.id === item.shellProductId)?.configurationFlows ?? []
}

/** Map one cart item to a wire product line. Returns null if it can't resolve a productCode. */
export function buildProduct(
  item: CartItem,
  catalogue: ResolvedCatalogue | undefined,
  attachments: Record<string, ReadonlyArray<{ gcFilename: string }>>,
  /** Order-level answers, resolved once for the whole cart. */
  shared: Record<string, unknown>,
  /** `productConfigs`. Optional: a hand-built state (tests, older drafts) may
   *  not carry the map at all, and a missing config is simply no configuration. */
  configs: Record<string, unknown> | undefined,
  /** The line is made from the order's shoot (`shootOutputLines`), so it carries no files or configuration. */
  fromShoot = false
): OrderProductDTO | null {
  const variant = findVariant(catalogue, item.variantId)
  // No resolvable product code → skip the line rather than emit an empty ref.
  if (!variant?.productCode) return null

  const lineFlows = lineConfigurationFlows(item, variant, catalogue)
  const optionsByCode = new Map((variant.options ?? []).map((o) => [o.productCode, o]))
  const addons = buildAddons(item, variant, catalogue).map((addon) => {
    const flows = optionsByCode.get(addon.productCode)?.configurationFlows ?? []
    if (flows.length === 0) return addon
    return compact<OrderAddonDTO>({ ...addon, configuration: buildConfiguration(item, configs, shared, flows, { only: flows }) })
  })
  // Ticked or not: an answer stored for an add-on's step never belongs to the line itself.
  const addonOnlyFlows = (variant.options ?? [])
    .flatMap((o) => o.configurationFlows ?? [])
    .filter((flow) => !lineFlows.includes(flow))

  return compact<OrderProductDTO>({
    productCode: variant.productCode,
    count: item.quantity,
    comment: item.note?.trim() || undefined,
    addons,
    dependentProducts: [], // SEAM — flat cart
    inputImages: fromShoot ? [] : buildInputImages(item, variant, attachments),
    configuration: fromShoot ? undefined : buildConfiguration(item, configs, shared, lineFlows, { exclude: addonOnlyFlows }),
    pack: item.pack ? { productCode: item.pack.productCode, selectionId: item.pack.selectionId } : undefined,
  })
}
